import { MatterBannerKind } from '@/matter/store/schemas'
import { useContext, useState } from 'react'

import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { useOpenConnectedAccountsSettings } from '@/settings-modal/hooks/useOpenConnectedAccountsSettings'
import { Banner } from '@/components/banner/Banner'
import { Button } from '@/components/button/Button'
import { kIsExternalStorageProviderEnabled, type ExternalStorageProvider } from '@/constants/constants-external-storage'
import { AuthContext } from '@/context/auth-context'
import { useMatterBannerDismissal } from '@/matter/hooks/useMatterBannerDismissal'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import { useDriveSyncSetupBanner } from '@/user_files/external-storage/hooks/useDriveSyncSetupBanner'

/** Provider logos previewed in the "Connect your files" banner, in the order the design shows them. */
const PREVIEW_PROVIDERS: ExternalStorageProvider[] = ['box', 'dropbox', 'onedrive', 'google_drive']

function ProviderLogoCluster() {
  const providers = PREVIEW_PROVIDERS.filter(kIsExternalStorageProviderEnabled)

  return (
    <div className="flex items-center">
      {providers.map((provider, index) => {
        const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]
        return (
          <div
            key={provider}
            className={`flex size-6 items-center justify-center rounded-full border border-brand-100 bg-white ${index < providers.length - 1 ? '-mr-1.5' : ''}`}
          >
            <ProviderIcon className="h-3.5 w-4" />
          </div>
        )
      })}
    </div>
  )
}

export type DriveSyncSetupBannerProps = {
  matterId: string
  /** Attributes the drive-sync funnel to the surface hosting this banner. */
  matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  /** Attributes the Settings page view the "Connect your files" prompt navigates to. */
  settingsEntrySource: SettingsEntrySource
  /** Spacing for the host surface; the banner itself carries no outer margin. */
  className?: string
}

/**
 * Drive-sync setup prompt for a matter: connect a DMS account, or pick the folder to sync once one
 * is connected. Renders nothing when the matter is already set up or the viewer can't configure it.
 * Shared by the matter overview and the drive view, which differ only in analytics attribution.
 */
export function DriveSyncSetupBanner({ matterId, matterDriveSyncEntrySource, settingsEntrySource, className }: DriveSyncSetupBannerProps) {
  const openConnectedAccountsSettings = useOpenConnectedAccountsSettings()
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const { kind, connectedAccount, mapping } = useDriveSyncSetupBanner(matterId)
  const { dismissedBanners, dismissedBannersLoaded, dismissBanner } = useMatterBannerDismissal(matterId)
  const [selectFolderOpen, setSelectFolderOpen] = useState(false)

  if (!kind) return null

  // Dismissal is per prompt and shared across every surface that hosts this banner, so clearing it
  // on the matter overview also clears it in the drive view. Waiting for the dismissal set keeps a
  // cleared prompt from flashing when this banner's status fetch resolves before the matter detail.
  if (!dismissedBannersLoaded) return null
  if (dismissedBanners.includes(kind)) return null
  const handleDismiss = () => dismissBanner(kind)

  // Pre-connection discovery — sends the user to Settings to link a DMS. Must not reuse
  // MatterDriveSyncSetupEntryClicked, which is reserved for post-connection folder-picker entry.
  const handleConnectFilesClick = () => {
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncDiscoveryClicked,
      {
        matterId,
        organizationId: getDriveSyncOrganizationId(authContext?.userAccountData),
        matterDriveSyncEntrySource,
      },
      trackEvent
    )
    openConnectedAccountsSettings(settingsEntrySource)
  }

  const handleConnectFolderClick = () => {
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
      {
        matterId,
        organizationId: getDriveSyncOrganizationId(authContext?.userAccountData),
        ...(connectedAccount ? { externalStorageProvider: connectedAccount.provider } : {}),
        matterDriveSyncEntrySource,
      },
      trackEvent
    )
    setSelectFolderOpen(true)
  }

  if (kind === MatterBannerKind.DriveSyncConnectFiles) {
    return (
      <div className={className}>
        <Banner
          icon={<ProviderLogoCluster />}
          title="Connect your files"
          description="Choose a provider and sync a folder"
          onDismiss={handleDismiss}
          action={
            <Button variant="tonal" color="primary" size="xs" onClick={handleConnectFilesClick}>
              Settings
            </Button>
          }
        />
      </div>
    )
  }

  // ConnectFolder — only reachable with a connected account, which the folder picker needs.
  if (!connectedAccount) return null
  const ConnectedProviderIcon = PROVIDER_ICON_COMPONENTS[connectedAccount.provider]

  return (
    <>
      <div className={className}>
        <Banner
          variant="neutral"
          icon={<ConnectedProviderIcon className="h-[28px] w-[30px]" />}
          title="No folder synced yet"
          description={`Sync a ${PROVIDER_COMPACT_DISPLAY_NAME[connectedAccount.provider]} folder to this matter`}
          onDismiss={handleDismiss}
          action={
            <Button variant="tonal" color="neutral" size="xs" onClick={handleConnectFolderClick}>
              Connect Folder
            </Button>
          }
        />
      </div>
      <SelectExternalFolderModal
        account={connectedAccount}
        currentExternalFolderId={mapping?.external_folder_id}
        matterId={matterId}
        open={selectFolderOpen}
        onOpenChange={setSelectFolderOpen}
        replaceExistingMapping={mapping != null}
        matterDriveSyncEntrySource={matterDriveSyncEntrySource}
      />
    </>
  )
}
